import { useCallback } from "react";

import {
	CurrencyType,
	type DayOfTheWeek,
	type SaleTypeForPlan,
	type SaleTypeForTerm,
} from "@/lib/gql/__generated__/graphql";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";

import type { DiscountLongTermStayFormModel } from "../../../long-term-stay/_shared/model/type";
import type { DiscountMultiFormModel } from "../../../multi/_shared/model/type";
import type { DiscountOrdinaryFormModel } from "../../../ordinary/_shared/model/type";

type ViewModel =
	| DiscountLongTermStayFormModel["ViewModel"]
	| DiscountOrdinaryFormModel["ViewModel"]
	| DiscountMultiFormModel["ViewModel"];

export const useSharedActions = <T extends ViewModel>(viewModel: T, onSetViewModel: (viewModel: T) => void) => {
	/**
	 * 公開にする
	 */
	const onPublish = useCallback(() => {
		onSetViewModel({
			...viewModel,
			inSale: true,
		});
	}, [viewModel, onSetViewModel]);
	/**
	 * 非公開にする
	 */
	const onUnpublish = useCallback(() => {
		onSetViewModel({
			...viewModel,
			inSale: false,
		});
	}, [viewModel, onSetViewModel]);
	/**
	 * ディスカウント名の変更
	 */
	const onChangeName = useCallback(
		(value: string) => {
			onSetViewModel({ ...viewModel, name: { ...viewModel.name, value } });
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウント説明の変更
	 */
	const onChangeDescription = useCallback(
		(value: string) => {
			onSetViewModel({ ...viewModel, description: { ...viewModel.description, value } });
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウントコードの変更
	 */
	const onChangeDiscountCode = useCallback(
		(value: string) => {
			onSetViewModel({ ...viewModel, discountCode: { ...viewModel.discountCode, value } });
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * プランにディスカウント適用可能の変更
	 */
	const onChangeIsPlanDiscountEligible = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isPlanDiscountEligible: value });
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * 部屋ディスカウント適用可能の変更
	 */
	const onChangeIsRoomDiscountEligible = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isRoomDiscountEligible: value });
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * オプションにディスカウント適用可能の変更
	 */
	const onChangeIsOptionDiscountEligible = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isOptionDiscountEligible: value });
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * 追加料金にディスカウント適用可能の変更
	 */
	const onChangeIsAdditionalFeeDiscountEligible = useCallback(
		(value: boolean) => {
			onSetViewModel({ ...viewModel, isAdditionalFeeDiscountEligible: value });
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウント上限金額を設定しない
	 */
	const onUnSettingDiscountPriceTotalLimit = useCallback(() => {
		onSetViewModel({
			...viewModel,
			discountPriceTotalLimit: undefined,
		});
	}, [onSetViewModel, viewModel]);
	/**
	 * ディスカウント上限金額の変更
	 */
	const onChangeDiscountPriceTotalLimit = useCallback(
		(amount: number) => {
			onSetViewModel({
				...viewModel,
				discountPriceTotalLimit: {
					amount: {
						value: amount,
						isEdited: viewModel.discountPriceTotalLimit?.amount?.isEdited ?? false,
						error: viewModel.discountPriceTotalLimit?.amount?.error,
					},
					currencyType: {
						value: CurrencyType.Jpy,
						isEdited: viewModel.discountPriceTotalLimit?.currencyType?.isEdited ?? false,
						error: viewModel.discountPriceTotalLimit?.currencyType?.error,
					},
				},
			});
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウントをプラン限定にするかの変更
	 */
	const onChangeSaleTypeForPlan = useCallback(
		(value: SaleTypeForPlan) => {
			onSetViewModel({
				...viewModel,
				saleTypeForPlan: value,
			});
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * キャンセル不可のディスカウントにするかの変更
	 */
	const onChangeIsUncancelable = useCallback(
		(value: boolean) => {
			onSetViewModel({
				...viewModel,
				isUncancelable: value,
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * ディスカウントの利用可能期間を設定するかの変更
	 */
	const onChangeSaleTypeForTerm = useCallback(
		(value: SaleTypeForTerm) => {
			onSetViewModel({
				...viewModel,
				saleTypeForTerm: value,
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 公開期間を変更
	 */
	const onChangeSale = useCallback(
		(value: DateRange) => {
			onSetViewModel({
				...viewModel,
				saleStart: value.from,
				saleEnd: value.to,
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 利用可能期間を変更
	 */
	const onChangeStayable = useCallback(
		(value: DateRange) => {
			onSetViewModel({
				...viewModel,
				stayableStart: value.from,
				stayableEnd: value.to,
			});
		},
		[viewModel, onSetViewModel],
	);
	/**
	 * 利用可能曜日の変更
	 */
	const onChangeStayableDayOfTheWeek = useCallback(
		(value: DayOfTheWeek) => {
			const stayableDayOfTheWeek = viewModel.stayableDayOfTheWeek ?? [];

			const newValue = stayableDayOfTheWeek.includes(value)
				? stayableDayOfTheWeek.filter((item) => item !== value)
				: stayableDayOfTheWeek.concat(value);

			onSetViewModel({
				...viewModel,
				stayableDayOfTheWeek: newValue,
			});
		},
		[viewModel, onSetViewModel],
	);

	return {
		//
		// 公開にする
		onPublish,
		// 非公開にする
		onUnpublish,

		// 基本設定
		// ディスカウント名の変更
		onChangeName,
		// ディスカウント説明の変更
		onChangeDescription,
		// ディスカウントコードの変更
		onChangeDiscountCode,
		// ディスカウント対象の変更
		onChangeIsPlanDiscountEligible,
		// 部屋のディスカウント対象の変更
		onChangeIsRoomDiscountEligible,
		// オプションのディスカウント対象の変更
		onChangeIsOptionDiscountEligible,
		// 追加料金のディスカウント対象の変更
		onChangeIsAdditionalFeeDiscountEligible,

		// 追加設定
		// ディスカウント上限金額を設定しない
		onUnSettingDiscountPriceTotalLimit,
		// ディスカウント上限金額の変更
		onChangeDiscountPriceTotalLimit,
		// ディスカウントをプラン限定にするかの変更
		onChangeSaleTypeForPlan,
		// キャンセル不可のディスカウントにするかの変更
		onChangeIsUncancelable,
		// ディスカウントの利用可能期間を設定するかの変更
		onChangeSaleTypeForTerm,
		// 公開期間を変更
		onChangeSale,
		// 利用可能期間を変更
		onChangeStayable,
		// 利用可能曜日の変更
		onChangeStayableDayOfTheWeek,
	};
};
